<script setup>
/* global axios */
// Email Marketing — wizard de criação de campanha (4 passos).
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const STEPS = ['source', 'template', 'settings', 'review'];
const step = ref('source');
const stepIndex = computed(() => STEPS.indexOf(step.value));
const saving = ref(false);
const error = ref('');

const SOURCES = ['contacts', 'csv', 'tags', 'kanban'];
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Passo 1 — origem
const sourceType = ref('contacts');
const csvContacts = ref([]);
const csvTotal = ref(0);
const csvLoading = ref(false);
const tagsList = ref([]);
const selectedTags = ref([]);
const funnels = ref([]);
const selectedFunnelId = ref('');
const selectedStageIds = ref([]);
const previewContacts = ref([]);
const previewLoading = ref(false);
const contactsList = ref([]);
const contactsLoading = ref(false);
const contactsSearch = ref('');
const selectedContactIds = ref(new Set());
const selectedContactsCount = ref(0);

// Passo 2 — template
const templates = ref([]);
const selectedTemplateId = ref(null);
const name = ref('');
const subjectOverride = ref('');
const fromEmail = ref('');
const fromName = ref('');
const replyTo = ref('');

// Passo 3 — configurações
const delayMin = ref(5);
const delayMax = ref(20);
const windowStart = ref('');
const windowEnd = ref('');
const allowedDays = ref([]);
const enableTracking = ref(true);
const startMode = ref('immediate');
const scheduledAt = ref('');
const previewHtml = ref('');
const previewHtmlLoading = ref(false);

const selectedFunnel = computed(() =>
  funnels.value.find(f => f.id === selectedFunnelId.value)
);

onMounted(async () => {
  try {
    templates.value =
      (await axios.get(`${base}/email_templates`)).data.data || [];
  } catch {
    templates.value = [];
  }
  try {
    tagsList.value =
      (await axios.get(`${base}/email_campaigns/tags`)).data.data || [];
  } catch {
    tagsList.value = [];
  }
  try {
    funnels.value = (
      (await axios.get(`${base}/funnels`)).data.data ||
      (await axios.get(`${base}/funnels`)).data ||
      []
    ).filter(f => !f.is_system);
  } catch {
    funnels.value = [];
  }
});

const loadContacts = async () => {
  contactsLoading.value = true;
  try {
    const q = contactsSearch.value
      ? `?search=${encodeURIComponent(contactsSearch.value)}`
      : '';
    contactsList.value =
      (await axios.get(`${base}/email_campaigns/sources_contacts${q}`)).data
        .data || [];
  } catch {
    contactsList.value = [];
  } finally {
    contactsLoading.value = false;
  }
};

const toggleContact = id => {
  const next = new Set(selectedContactIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedContactIds.value = next;
  selectedContactsCount.value = next.size;
};
const isContactSelected = id => selectedContactIds.value.has(id);
const selectAllContacts = () => {
  selectedContactIds.value = new Set(contactsList.value.map(c => c.id));
  selectedContactsCount.value = selectedContactIds.value.size;
};
const clearContacts = () => {
  selectedContactIds.value = new Set();
  selectedContactsCount.value = 0;
};

const onCsvFile = async e => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;
  csvLoading.value = true;
  error.value = '';
  try {
    const fd = new FormData();
    fd.append('file', file);
    const d = (await axios.post(`${base}/email_campaigns/upload_csv`, fd)).data;
    csvContacts.value = d.contacts || [];
    csvTotal.value = d.total || csvContacts.value.length;
  } catch {
    error.value = t('EMAIL.wizard.errors.csv');
  } finally {
    csvLoading.value = false;
  }
};

const toggleTag = title => {
  selectedTags.value = selectedTags.value.includes(title)
    ? selectedTags.value.filter(x => x !== title)
    : [...selectedTags.value, title];
};
const toggleStage = id => {
  selectedStageIds.value = selectedStageIds.value.includes(id)
    ? selectedStageIds.value.filter(x => x !== id)
    : [...selectedStageIds.value, id];
};
const toggleDay = d => {
  allowedDays.value = allowedDays.value.includes(d)
    ? allowedDays.value.filter(x => x !== d)
    : [...allowedDays.value, d];
};

const sourceConfig = () => {
  if (sourceType.value === 'tags') return { tags: selectedTags.value };
  if (sourceType.value === 'kanban')
    return { stage_ids: selectedStageIds.value };
  if (sourceType.value === 'contacts')
    return { contact_ids: Array.from(selectedContactIds.value) };
  return {};
};
const backendSourceType = () => {
  if (sourceType.value === 'kanban') return 'kanban_stage';
  if (sourceType.value === 'contacts') return 'chatwoot_contacts';
  return sourceType.value;
};

const loadPreview = async () => {
  previewLoading.value = true;
  previewContacts.value = [];
  try {
    const d = (
      await axios.post(`${base}/email_campaigns/preview_source`, {
        source_type: backendSourceType(),
        source_config: sourceConfig(),
      })
    ).data.data;
    previewContacts.value = d.preview || [];
  } catch {
    previewContacts.value = [];
  } finally {
    previewLoading.value = false;
  }
};

const interpolate = html =>
  (html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k) => {
    const map = {
      nome: 'Maria Silva',
      name: 'Maria Silva',
      email: 'maria@exemplo.com',
    };
    return map[k.toLowerCase()] || '';
  });

const loadEmailPreview = async () => {
  if (!selectedTemplateId.value) return;
  previewHtmlLoading.value = true;
  const startedAt = Date.now();
  try {
    const tpl = (
      await axios.get(`${base}/email_templates/${selectedTemplateId.value}`)
    ).data.data;
    const inner = interpolate(tpl.html);
    // Embrulha o fragmento num documento HTML completo para o iframe renderizar de forma consistente.
    previewHtml.value = inner
      ? `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>${inner}</html>`
      : '';
  } catch {
    previewHtml.value = '';
  } finally {
    // Mantém o spinner visível por no mínimo 400ms para dar feedback ao clicar "Atualizar prévia".
    const elapsed = Date.now() - startedAt;
    if (elapsed < 400)
      await new Promise(resolve => setTimeout(resolve, 400 - elapsed));
    previewHtmlLoading.value = false;
  }
};

// Carrega a prévia automaticamente ao entrar no passo de revisão (não fica em branco).
watch(step, s => {
  if (s === 'review' && selectedTemplateId.value) loadEmailPreview();
});

const selectedTemplate = computed(() =>
  templates.value.find(tp => tp.id === selectedTemplateId.value)
);

const reviewRows = computed(() => {
  const srcLabel = {
    contacts: t('EMAIL.wizard.source.contacts'),
    csv: t('EMAIL.wizard.source.csv'),
    tags: t('EMAIL.wizard.source.tags'),
    kanban: t('EMAIL.wizard.source.kanban'),
  }[sourceType.value];
  const tpl = selectedTemplate.value;
  return [
    [t('EMAIL.wizard.review.name'), name.value || '—'],
    [t('EMAIL.wizard.review.source'), srcLabel],
    [t('EMAIL.wizard.review.template'), tpl ? tpl.name : '—'],
    [
      t('EMAIL.wizard.review.subject'),
      subjectOverride.value || (tpl ? tpl.subject : '—'),
    ],
    [
      t('EMAIL.wizard.review.tracking'),
      enableTracking.value
        ? t('EMAIL.wizard.review.on')
        : t('EMAIL.wizard.review.off'),
    ],
    [t('EMAIL.wizard.review.delay'), `${delayMin.value}–${delayMax.value}s`],
    [
      t('EMAIL.wizard.review.start'),
      startMode.value === 'immediate'
        ? t('EMAIL.wizard.settings.immediate')
        : scheduledAt.value || '—',
    ],
  ];
});

const selectTemplate = tpl => {
  selectedTemplateId.value = tpl.id;
  subjectOverride.value = tpl.subject;
};

// Validação de cada passo — retorna a mensagem de erro (ou '' se ok).
const stepValidationError = () => {
  if (step.value === 'source') {
    if (sourceType.value === 'csv' && csvTotal.value === 0)
      return t('EMAIL.wizard.errors.csvRequired');
    if (sourceType.value === 'tags' && !selectedTags.value.length)
      return t('EMAIL.wizard.errors.tagsRequired');
    if (sourceType.value === 'kanban' && !selectedStageIds.value.length)
      return t('EMAIL.wizard.errors.stagesRequired');
  }
  if (step.value === 'template') {
    if (!name.value.trim()) return t('EMAIL.wizard.errors.name');
    if (!selectedTemplateId.value) return t('EMAIL.wizard.errors.template');
  }
  if (step.value === 'settings') {
    if (delayMin.value < 1 || delayMax.value < 1)
      return t('EMAIL.wizard.errors.delayInvalid');
    if (delayMin.value > delayMax.value)
      return t('EMAIL.wizard.errors.delayOrder');
    if (startMode.value === 'scheduled' && !scheduledAt.value)
      return t('EMAIL.wizard.errors.scheduledRequired');
  }
  return '';
};
// Botão "Próximo" desabilitado enquanto o passo atual estiver incompleto.
const canAdvance = computed(() => !stepValidationError());

const next = () => {
  const err = stepValidationError();
  if (err) {
    error.value = err;
    return;
  }
  error.value = '';
  if (stepIndex.value < STEPS.length - 1)
    step.value = STEPS[stepIndex.value + 1];
};
const prev = () => {
  error.value = '';
  if (step.value === 'source') router.push({ name: 'kanban_email' });
  else step.value = STEPS[stepIndex.value - 1];
};

const submit = async launch => {
  if (!name.value) {
    error.value = t('EMAIL.wizard.errors.name');
    return;
  }
  if (!selectedTemplateId.value) {
    error.value = t('EMAIL.wizard.errors.template');
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const payload = {
      name: name.value,
      template_id: selectedTemplateId.value,
      subject: subjectOverride.value || undefined,
      from_email: fromEmail.value || undefined,
      from_name: fromName.value || undefined,
      reply_to: replyTo.value || undefined,
      source_type: backendSourceType(),
      source_config: sourceConfig(),
      enable_tracking: enableTracking.value,
      delay_min_seconds: delayMin.value,
      delay_max_seconds: delayMax.value,
      window_start: windowStart.value || undefined,
      window_end: windowEnd.value || undefined,
      allowed_days: allowedDays.value.length ? allowedDays.value : undefined,
      scheduled_at:
        startMode.value === 'scheduled' && scheduledAt.value
          ? new Date(scheduledAt.value).toISOString()
          : undefined,
    };
    if (sourceType.value === 'csv') payload.contacts = csvContacts.value;
    const created = (await axios.post(`${base}/email_campaigns`, payload)).data
      .data;
    if (launch && created && created.id)
      await axios.post(`${base}/email_campaigns/${created.id}/launch`);
    router.push({ name: 'kanban_email' });
  } catch (e) {
    error.value = e?.response?.data?.error || t('EMAIL.wizard.errors.create');
    saving.value = false;
  }
};

const inputCls =
  'block w-full px-3 py-2 text-sm rounded-lg border-0 outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 placeholder:text-n-slate-10 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:ring-0';
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- header -->
    <header
      class="flex items-center gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        @click="router.push({ name: 'kanban_email' })"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
      </button>
      <h1 class="text-heading-1 text-n-slate-12">
        {{ t('EMAIL.wizard.title') }}
      </h1>
    </header>

    <!-- stepper (circular, igual ao wizard de funil) -->
    <div
      class="flex items-center max-w-3xl px-6 py-4 mx-auto border-b border-n-weak bg-n-solid-2 w-full"
    >
      <template v-for="(s, i) in STEPS" :key="s">
        <div class="flex flex-col items-center gap-1.5">
          <div
            class="grid text-sm font-semibold transition-all rounded-full w-9 h-9 place-items-center"
            :class="
              i < stepIndex
                ? 'bg-n-brand text-white'
                : i === stepIndex
                  ? 'bg-n-brand text-white ring-4 ring-n-brand/20'
                  : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            <svg
              v-if="i < stepIndex"
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M20 6 9 17l-5-5"
              />
            </svg>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span
            class="text-xs whitespace-nowrap"
            :class="
              i === stepIndex
                ? 'text-n-slate-12 font-medium'
                : 'text-n-slate-10'
            "
            >{{ t(`EMAIL.wizard.steps.${s}`) }}</span
          >
        </div>
        <div
          v-if="i < STEPS.length - 1"
          class="flex-1 h-0.5 mx-2 mb-5 rounded transition-colors"
          :class="i < stepIndex ? 'bg-n-brand' : 'bg-n-alpha-2'"
        />
      </template>
    </div>

    <!-- content -->
    <div class="flex-1 px-6 py-8 overflow-y-auto">
      <p
        v-if="error"
        class="max-w-3xl mx-auto mb-4 text-sm font-medium text-n-ruby-11"
      >
        {{ error }}
      </p>

      <!-- STEP 1 — origem -->
      <div
        v-if="step === 'source'"
        class="w-full max-w-3xl p-6 mx-auto space-y-6 border shadow-sm rounded-xl border-n-weak bg-n-solid-1"
      >
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.source.label')
          }}</label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="s in SOURCES"
              :key="s"
              class="p-3 text-sm font-medium text-left rounded-lg border transition-colors"
              :class="
                sourceType === s
                  ? 'border-n-brand text-n-brand bg-n-alpha-2'
                  : 'border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1'
              "
              @click="sourceType = s"
            >
              {{ t(`EMAIL.wizard.source.${s}`) }}
            </button>
          </div>
        </div>

        <!-- csv -->
        <div v-if="sourceType === 'csv'">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.source.csvLabel')
          }}</label>
          <p class="mb-2 text-xs text-n-slate-10">
            {{ t('EMAIL.wizard.source.csvHint') }}
          </p>
          <input
            type="file"
            accept=".csv"
            class="block text-sm text-n-slate-11"
            @change="onCsvFile"
          />
          <p v-if="csvLoading" class="mt-1 text-xs text-n-slate-10">
            {{ t('EMAIL.wizard.source.csvProcessing') }}
          </p>
          <p v-else-if="csvTotal > 0" class="mt-1 text-xs text-n-slate-11">
            {{ t('EMAIL.wizard.source.csvLoaded', { count: csvTotal }) }}
          </p>
        </div>

        <!-- contacts -->
        <div v-if="sourceType === 'contacts'" class="space-y-3">
          <div class="flex items-center gap-2">
            <input
              v-model="contactsSearch"
              :class="inputCls"
              :placeholder="t('EMAIL.wizard.source.searchPlaceholder')"
              @keydown.enter="loadContacts"
            />
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 shrink-0"
              :disabled="contactsLoading"
              @click="loadContacts"
            >
              {{
                contactsLoading
                  ? t('EMAIL.wizard.source.loading')
                  : t('EMAIL.wizard.source.loadBtn')
              }}
            </button>
          </div>

          <div v-if="contactsList.length">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs text-n-slate-10">{{
                selectedContactsCount === 0
                  ? t('EMAIL.wizard.source.loadedCount', {
                      count: contactsList.length,
                    })
                  : t('EMAIL.wizard.source.selectedCount', {
                      count: selectedContactsCount,
                    })
              }}</span>
              <div class="flex gap-2 text-xs">
                <button
                  class="text-n-brand hover:underline"
                  @click="selectAllContacts"
                >
                  {{ t('EMAIL.wizard.source.selectAll') }}
                </button>
                <span class="text-n-slate-10">|</span>
                <button
                  class="text-n-brand hover:underline"
                  @click="clearContacts"
                >
                  {{ t('EMAIL.wizard.source.clear') }}
                </button>
              </div>
            </div>
            <div
              class="overflow-y-auto border rounded-lg border-n-weak max-h-72"
            >
              <table class="w-full text-xs">
                <thead>
                  <tr class="text-left border-b border-n-weak text-n-slate-10">
                    <th class="w-8 px-3 py-2" />
                    <th class="px-3 py-2 font-medium">
                      {{ t('EMAIL.wizard.source.colName') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ t('EMAIL.wizard.source.colEmail') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="c in contactsList"
                    :key="c.id"
                    class="border-b cursor-pointer border-n-weak hover:bg-n-alpha-1"
                    @click="toggleContact(c.id)"
                  >
                    <td class="px-3 py-2">
                      <input
                        type="checkbox"
                        :checked="isContactSelected(c.id)"
                        @click.stop="toggleContact(c.id)"
                      />
                    </td>
                    <td class="px-3 py-2 text-n-slate-12">
                      {{ c.name || '—' }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">{{ c.email }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('EMAIL.wizard.source.noneMeansAll') }}
            </p>
          </div>
          <p v-else-if="!contactsLoading" class="text-xs text-n-slate-10">
            {{ t('EMAIL.wizard.source.loadHint') }}
          </p>
        </div>

        <!-- tags -->
        <div v-if="sourceType === 'tags'">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.source.tagsLabel')
          }}</label>
          <div class="flex flex-wrap gap-2 mt-2">
            <button
              v-for="tag in tagsList"
              :key="tag.id"
              class="px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
              :class="
                selectedTags.includes(tag.title)
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="toggleTag(tag.title)"
            >
              {{ tag.title }}
            </button>
            <span v-if="!tagsList.length" class="text-xs text-n-slate-10">{{
              t('EMAIL.wizard.source.noTags')
            }}</span>
          </div>
        </div>

        <!-- kanban -->
        <div v-if="sourceType === 'kanban'" class="space-y-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.wizard.source.funnelLabel')
            }}</label>
            <select
              v-model="selectedFunnelId"
              :class="inputCls"
              @change="selectedStageIds = []"
            >
              <option value="">
                {{ t('EMAIL.wizard.source.funnelPlaceholder') }}
              </option>
              <option v-for="f in funnels" :key="f.id" :value="f.id">
                {{ f.name }}
              </option>
            </select>
          </div>
          <div v-if="selectedFunnel">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.wizard.source.stagesLabel')
            }}</label>
            <div class="flex flex-wrap gap-2 mt-1">
              <button
                v-for="stage in selectedFunnel.stages || []"
                :key="stage.id"
                class="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                :class="
                  selectedStageIds.includes(stage.id)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
                @click="toggleStage(stage.id)"
              >
                {{ stage.name }}
              </button>
            </div>
          </div>
        </div>

        <button
          v-if="sourceType === 'tags' || sourceType === 'kanban'"
          class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
          :disabled="previewLoading"
          @click="loadPreview"
        >
          {{
            previewLoading
              ? t('EMAIL.wizard.source.loading')
              : t('EMAIL.wizard.source.previewBtn')
          }}
        </button>

        <div v-if="previewContacts.length && sourceType !== 'contacts'">
          <p class="mb-2 text-xs text-n-slate-10">
            {{ t('EMAIL.wizard.source.previewTitle') }}
          </p>
          <table class="w-full text-xs">
            <thead>
              <tr class="text-left border-b border-n-weak text-n-slate-10">
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.wizard.source.colName') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.wizard.source.colEmail') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(c, i) in previewContacts"
                :key="i"
                class="border-b border-n-weak"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ c.name || '—' }}</td>
                <td class="px-3 py-2 text-n-slate-11">{{ c.email }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- STEP 2 — template -->
      <div
        v-else-if="step === 'template'"
        class="w-full max-w-3xl p-6 mx-auto space-y-5 border shadow-sm rounded-xl border-n-weak bg-n-solid-1"
      >
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.template.name')
          }}</label>
          <input
            v-model="name"
            :class="inputCls"
            :placeholder="t('EMAIL.wizard.template.namePlaceholder')"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.template.label')
          }}</label>
          <div class="grid grid-cols-2 gap-3 mt-2">
            <button
              v-for="tpl in templates"
              :key="tpl.id"
              class="p-3 text-left rounded-lg border transition-colors"
              :class="
                selectedTemplateId === tpl.id
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak bg-n-solid-2 hover:bg-n-alpha-1'
              "
              @click="selectTemplate(tpl)"
            >
              <div class="text-sm font-medium text-n-slate-12">
                {{ tpl.name }}
              </div>
              <div class="text-xs truncate text-n-slate-10">
                {{ tpl.subject }}
              </div>
            </button>
            <div
              v-if="!templates.length"
              class="col-span-2 py-8 text-sm text-center text-n-slate-10"
            >
              {{ t('EMAIL.wizard.template.none') }}
              <button
                class="underline text-n-brand"
                @click="router.push({ name: 'kanban_email_template_new' })"
              >
                {{ t('EMAIL.wizard.template.create') }}
              </button>
            </div>
          </div>
        </div>

        <template v-if="selectedTemplateId">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.wizard.template.subject')
            }}</label>
            <input v-model="subjectOverride" :class="inputCls" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.wizard.template.fromEmail')
              }}</label>
              <input v-model="fromEmail" :class="inputCls" />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.wizard.template.fromName')
              }}</label>
              <input v-model="fromName" :class="inputCls" />
            </div>
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.wizard.template.replyTo')
            }}</label>
            <input v-model="replyTo" :class="inputCls" />
          </div>
        </template>
      </div>

      <!-- STEP 3 — configurações -->
      <div
        v-else-if="step === 'settings'"
        class="w-full max-w-3xl p-6 mx-auto space-y-6 border shadow-sm rounded-xl border-n-weak bg-n-solid-1"
      >
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.settings.delay')
          }}</label>
          <div class="flex items-center gap-3 mt-1">
            <div class="flex-1">
              <label class="text-xs text-n-slate-10">{{
                t('EMAIL.wizard.settings.delayMin')
              }}</label>
              <input
                v-model.number="delayMin"
                type="number"
                min="1"
                :class="inputCls"
              />
            </div>
            <div class="flex-1">
              <label class="text-xs text-n-slate-10">{{
                t('EMAIL.wizard.settings.delayMax')
              }}</label>
              <input
                v-model.number="delayMax"
                type="number"
                min="1"
                :class="inputCls"
              />
            </div>
          </div>
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.settings.window')
          }}</label>
          <div class="flex items-center gap-3 mt-1">
            <input v-model="windowStart" type="time" :class="inputCls" />
            <span class="text-sm text-n-slate-10">{{
              t('EMAIL.wizard.settings.to')
            }}</span>
            <input v-model="windowEnd" type="time" :class="inputCls" />
          </div>
        </div>
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.settings.days')
          }}</label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="d in DAYS"
              :key="d"
              class="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              :class="
                allowedDays.includes(d)
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
              @click="toggleDay(d)"
            >
              {{ t(`EMAIL.days.${d}`) }}
            </button>
          </div>
        </div>
        <label
          class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
        >
          <input v-model="enableTracking" type="checkbox" />
          {{ t('EMAIL.wizard.settings.tracking') }}
        </label>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('EMAIL.wizard.settings.start')
          }}</label>
          <div class="flex gap-3">
            <label
              v-for="m in ['immediate', 'scheduled']"
              :key="m"
              class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
            >
              <input
                type="radio"
                :checked="startMode === m"
                @change="startMode = m"
              />
              {{ t(`EMAIL.wizard.settings.${m}`) }}
            </label>
          </div>
          <input
            v-if="startMode === 'scheduled'"
            v-model="scheduledAt"
            type="datetime-local"
            :class="inputCls"
            class="mt-2"
          />
        </div>
      </div>

      <!-- STEP 4 — revisão (2 colunas: resumo | preview) -->
      <div v-else class="grid w-full max-w-5xl gap-5 mx-auto md:grid-cols-2">
        <!-- resumo -->
        <div
          class="p-6 space-y-4 border shadow-sm rounded-xl border-n-weak bg-n-solid-1"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('EMAIL.wizard.review.title') }}
          </h2>
          <div
            class="border divide-y rounded-lg border-n-weak divide-n-weak bg-n-solid-2"
          >
            <div
              v-for="row in reviewRows"
              :key="row[0]"
              class="flex items-center gap-4 px-4 py-2.5 text-sm"
            >
              <span class="shrink-0 w-28 text-n-slate-10">{{ row[0] }}</span>
              <span class="font-medium truncate text-n-slate-12">{{
                row[1]
              }}</span>
            </div>
          </div>
        </div>
        <!-- preview -->
        <div
          class="flex flex-col p-6 border shadow-sm rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('EMAIL.wizard.review.iframeTitle')
            }}</span>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 shrink-0 disabled:opacity-60"
              :disabled="previewHtmlLoading || !selectedTemplateId"
              @click="loadEmailPreview"
            >
              <span
                :class="
                  previewHtmlLoading
                    ? 'i-ph-circle-notch animate-spin'
                    : 'i-ph-arrows-clockwise'
                "
                class="size-3.5 shrink-0"
              />
              <span>{{ t('EMAIL.wizard.review.refreshPreview') }}</span>
            </button>
          </div>
          <div
            v-if="previewHtml"
            class="flex-1 min-h-[420px] overflow-hidden border rounded-lg border-n-weak"
          >
            <iframe
              :srcdoc="previewHtml"
              class="w-full h-full border-0"
              :title="t('EMAIL.wizard.review.iframeTitle')"
              sandbox="allow-same-origin"
            />
          </div>
          <div
            v-else
            class="grid flex-1 min-h-[420px] text-sm border border-dashed rounded-lg place-items-center border-n-weak text-n-slate-10"
          >
            {{ t('EMAIL.wizard.review.iframeTitle') }}
          </div>
        </div>
      </div>
    </div>

    <!-- footer nav -->
    <div class="border-t border-n-weak bg-n-solid-2">
      <div
        class="flex items-center justify-between w-full max-w-5xl px-6 py-4 mx-auto"
      >
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
          @click="prev"
        >
          {{
            step === 'source' ? t('EMAIL.cancel') : t('EMAIL.wizard.previous')
          }}
        </button>
        <div class="flex gap-2">
        <template v-if="step === 'review'">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            :disabled="saving"
            @click="submit(false)"
          >
            {{ t('EMAIL.wizard.review.saveDraft') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110"
            :disabled="saving"
            @click="submit(true)"
          >
            {{ t('EMAIL.wizard.review.createStart') }}
          </button>
        </template>
        <button
          v-else
          class="px-4 py-2 text-sm font-medium text-white transition-opacity rounded-lg bg-n-brand hover:brightness-110"
          :class="{ 'opacity-50': !canAdvance }"
          @click="next"
        >
          {{ t('EMAIL.wizard.next') }}
        </button>
        </div>
      </div>
    </div>
  </div>
</template>
